import * as React from 'react'

import {
  BanIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CircleCheckIcon,
  CircleIcon,
  RepeatIcon,
  SquarePenIcon,
  TriangleAlertIcon,
} from 'lucide-react'
import { action, computed, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import {
  BicCompletionMode,
  IPermitTypeField,
  IWorkflowStep,
  PermitFieldType,
  SourceEntityType,
} from '~/client/graph'
import BookingDeadlineDialog from '~/client/src/shared/components/BookingDeadlineDialog/BookingDeadlineDialog'
import PermitFieldIcon from '~/client/src/shared/components/PermitFieldIcon/PermitFieldIcon'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Progress } from '~/client/src/shared/components/shadcn-ui/progress'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import WorkflowStepNameLabel from '~/client/src/shared/components/WorkflowStepNameLabel'
import FileType from '~/client/src/shared/enums/FileType'

import { getWorkflowStepLevel } from '../../constants/formStatusesTags'
import { getWorkflowStepDisplayName } from '../../localization/enumDisplayTexts'
import Localization from '../../localization/LocalizationManager'
import { getBicCompletionModeLabel } from '../../models/WorkflowType'
import CompaniesStore from '../../stores/domain/Companies.store'
import { FileUploadingStore } from '../../stores/domain/FileUploading.store'
import LocationAttributesStore from '../../stores/domain/LocationAttributes.store'
import MessagesStore from '../../stores/domain/MessagesStore/Messages.store'
import PhotosStore from '../../stores/domain/Photos.store'
import ProjectMembersStore from '../../stores/domain/ProjectMembers.store'
import SitePermitFollowingsStore from '../../stores/domain/SitePermitFollowings.store'
import TagsStore from '../../stores/domain/Tags.store'
import ThreadsStore from '../../stores/domain/ThreadsStore/Threads.store'
import UserProjectsStore from '../../stores/domain/UserProjects.store'
import WorkflowTypesStore from '../../stores/domain/WorkflowTypes.store'
import { SHOW_FULLSCREEN_PREVIEW } from '../../stores/EventStore/eventConstants'
import EventsStore from '../../stores/EventStore/Events.store'
import PermitBallInCourtStore from '../../stores/ui/PermitBallInCourt.store'
import IAttachmentAdditionalInfo from '../../types/IAttachmentAdditionalInfo'
import { NOOP } from '../../utils/noop'
import NonWorkTimeConfirmDialog from '../NonWorkTimeConfirmDialog/NonWorkTimeConfirmDialog'
import PermitLogActionBarStore from '../PermitLogActionBar.store'
import StruxhubInput from '../StruxhubInputs/StruxhubInput'
import StruxhubTextValueSelector from '../StruxhubInputs/StruxhubSelector/StruxhubTextValueSelector'
import Tabs from '../Tabs/Tabs'
import WorkflowTypeIcon from '../WorkflowTypeIcon/WorkflowTypeIcon'
import ClosuresConflictDialog from './ClosuresConflictDialog/ClosuresConflictDialog'
import FormLogContent from './components/FormLogContent'
import InspectionPopupForm from './components/InspectionPopupForm/InspectionPopupForm'
import SubmissionStatusList from './components/PermitActionBar/components/SubmissionStatusList'
import PermitContentPickers from './components/PermitContentPickers/PermitContentPickers'
import RecurringWorkflowOptionsDatePicker from './components/PermitFormFields/RecurringWorkflowOptionsDatePicker'
import PermitFormFieldWrapper from './components/PermitFormFieldWrapper'
import PermitInspectionsSection from './components/PermitInspectionsSection'
import WorkflowSummary from './components/WorkflowSummary'
import HoursOfOperationDialog from './HoursOfOperationDialog/HoursOfOperationDialog'
import SitePermitCreationFormStore, {
  FormDetailsTab,
} from './WorkflowCreationForm.store'

import './SitePermitCreationFormContent.scss'

interface IProps {
  isReadonly: boolean
  store: SitePermitCreationFormStore
  FileInputType
  shouldHideResponsible?: boolean
  onPermitClose?: () => void
  isViewMode?: boolean
  toggleMode?: React.MouseEventHandler<HTMLButtonElement>
  isTypeReadOnly?: boolean
  isPreviewMode?: boolean

  eventsStore?: EventsStore
  permitLogActionBarStore?: PermitLogActionBarStore
  threadsStore?: ThreadsStore
  messagesStore?: MessagesStore
  photosStore?: PhotosStore
  fileUploadingStore?: FileUploadingStore
  tagsStore?: TagsStore
  userProjectsStore?: UserProjectsStore
  workflowFollowingsStore?: SitePermitFollowingsStore
  locationAttributesStore?: LocationAttributesStore
  workflowTypesStore?: WorkflowTypesStore
  companiesStore?: CompaniesStore
  projectMembersStore?: ProjectMembersStore
  permitBicStore?: PermitBallInCourtStore
  allowedTypeIds?: string[]
}

const formType = Localization.translator.formType
const _title = Localization.translator.title
const noAvailableTemplates = Localization.translator.noAvailableTemplates
const editForm = Localization.translator.editForm
const formEntityName = 'Form'

const WARNING_ICON_SIZE = 20
const STEP_ICON_SIZE = 20

@inject(
  'eventsStore',
  'threadsStore',
  'messagesStore',
  'photosStore',
  'fileUploadingStore',
  'tagsStore',
  'userProjectsStore',
  'workflowFollowingsStore',
  'locationAttributesStore',
  'workflowTypesStore',
  'companiesStore',
  'projectMembersStore',
)
@observer
export default class SitePermitCreationFormContent extends React.Component<IProps> {
  @observable private localProps: IProps = null
  private readonly actionBarStore: PermitLogActionBarStore

  private readonly clearPostEventCallback: () => void = NOOP

  private containerRef: HTMLDivElement
  private workflowStepRef: HTMLDivElement
  @observable private isDragOver: boolean = false
  @observable private stepProgressCollapsed: Map<string, boolean> = new Map()

  public constructor(props: IProps) {
    super(props)
    this.localProps = props

    const location =
      props.store.editablePermit.locations?.[0] &&
      props.locationAttributesStore.getLocationBySiteLocationObj(
        props.store.editablePermit.locations[0],
      )
    const attachmentAdditionalInfo: IAttachmentAdditionalInfo = {
      sourceEntityId: props.store.editablePermit.id,
      sourceEntityType: SourceEntityType.Form,
      locationIds: props.store.editablePermit.locations.map(l => l.id),
      locationName: location?.name,
      projectId: props.store.editablePermit.projectId,
      sourceEntityCode: props.store.editablePermit.code,
      sourceEntityTypeName: props.store.editablePermit.getNameOfPermitType(
        props.workflowTypesStore,
      ),
    }

    this.actionBarStore =
      props.permitLogActionBarStore ||
      new PermitLogActionBarStore(
        props.eventsStore,
        props.threadsStore,
        props.messagesStore,
        props.photosStore,
        props.fileUploadingStore,
        props.userProjectsStore,
        props.tagsStore,
        props.workflowFollowingsStore,
        attachmentAdditionalInfo,
      )

    this.clearPostEventCallback = props.eventsStore.addPostEventCallback(
      props.store.onPermitUpdated,
    )
    makeObservable(this)
  }

  public componentWillUnmount() {
    this.props.eventsStore.appState.preSelectedPermitTypeId = null
    this.clearPostEventCallback()

    document.removeEventListener('paste', this.handlePaste)
  }

  public componentDidMount() {
    const { setScrollToBottomFn, markPermitAsNonWorkTimeIfNeed } =
      this.props.store

    setScrollToBottomFn(this.scrollToBottom)

    Promise.resolve().then(() => this.scrollToBottom())

    markPermitAsNonWorkTimeIfNeed()

    document.addEventListener('paste', this.handlePaste)
  }

  @action.bound
  private toggleStepProgressCollapsed(stepId: string) {
    const newMap = new Map(this.stepProgressCollapsed)
    newMap.set(stepId, !newMap.get(stepId))
    this.stepProgressCollapsed = newMap
  }

  public render() {
    const { store, isReadonly, isPreviewMode } = this.localProps
    const {
      isNonWorkTimesModalOpened,
      isDeadlineModalShown,
      isClosuresConflictModalShown,
      shouldBlockLateRequesting,
      bookingDeadlineInterval,
      willBlockSubmitOnNonWorkTimeApply,
      formDetailTabs,
      activeDetailsTab,
      nonWorkTimeAndDeadlineTitle,
      nonWorkTimeAndDeadlineMessage,
      switchActiveTab,
      setRestrictionsAndBlockSubmitIfNeed,
      moveDatesOrSetAsLate,
      closeClosuresConflictModal,
    } = store
    const shouldShowDeadlineModal =
      !isNonWorkTimesModalOpened && isDeadlineModalShown

    return (
      <>
        <InspectionPopupForm isReadOnly={isReadonly} store={store} />
        <NonWorkTimeConfirmDialog
          isShown={isNonWorkTimesModalOpened}
          onHide={this.onHideNonWorkTimeDialog}
          onApply={setRestrictionsAndBlockSubmitIfNeed}
          shouldBlockSubmit={willBlockSubmitOnNonWorkTimeApply}
          entityName={formEntityName}
          title={nonWorkTimeAndDeadlineTitle}
          message={nonWorkTimeAndDeadlineMessage}
        />
        <BookingDeadlineDialog
          isShown={shouldShowDeadlineModal}
          shouldBlockLateRequesting={shouldBlockLateRequesting}
          deadlineInterval={bookingDeadlineInterval}
          onHide={this.onHideDeadlineModal}
          onApply={moveDatesOrSetAsLate}
        />
        <ClosuresConflictDialog
          isShown={isClosuresConflictModalShown}
          isRecurring={!!store.workflowFieldsStore.recurringSettings}
          onContinue={store.continueDespiteClosuresConflict}
          onCancel={closeClosuresConflictModal}
          onApply={closeClosuresConflictModal}
        />
        <HoursOfOperationDialog store={store} />
        <PermitContentPickers
          store={store}
          isPreviewMode={isPreviewMode}
          allowedTypeIds={this.props.allowedTypeIds}
        />
        <RecurringWorkflowOptionsDatePicker store={store} />

        {this.isExistingAndPublishedForm && (
          <>
            <Tabs
              tabObjects={formDetailTabs}
              activeTabId={activeDetailsTab}
              onTabClick={switchActiveTab}
              isFlexible
              className="form-details-tabs y-end pt8"
              tabClassName="tab h36 bradt8 relative bb-palette-brand-lighter"
              tabTextClassName="tab-text text light large bold lp015 center row y-center no-grow full-height"
              data-testid="form-details-tabs"
            />
            {this.isRecurringWorkflow && (
              <div className="row x-between pl16 bb-light-cool-grey z-index-5 top0 sticky bg-white tw:min-h-8">
                <div className="text bold large row y-center">
                  <RepeatIcon size={16} className="tw:mr-2 tw:shrink-0" />
                  {Localization.translator.recurringWorkflow}
                </div>
              </div>
            )}
          </>
        )}
        <div
          className={classList({
            'permit-form-fields full-height scrollable relative': true,
            'drag-over': this.isDragOver,
          })}
          ref={this.setContainerRef}
          onDrop={this.handleDrop}
          onDragOver={this.handleDragOver}
          onDragEnter={this.handleDragEnter}
          onDragLeave={this.handleDragLeave}
        >
          {this.tabContent}
        </div>
      </>
    )
  }

  private get tabContent(): JSX.Element {
    const { template, activeDetailsTab } = this.props.store

    if (!template) {
      return (
        <span className="row x-center text large bold pt24">
          <TriangleAlertIcon
            size={WARNING_ICON_SIZE}
            className="text orange mr10"
          />
          {noAvailableTemplates}...
        </span>
      )
    }
    if (activeDetailsTab === FormDetailsTab.LOG) {
      return this.logContent
    }
    if (activeDetailsTab === FormDetailsTab.FORM) {
      return this.formFields
    }
    return null
  }

  private get logContent(): JSX.Element {
    const { store, FileInputType } = this.props

    if (store.isLogDataLoading) {
      return (
        <div className="full-height full-width row x-center y-center">
          <Spinner size="lg" />
        </div>
      )
    }
    return (
      <FormLogContent
        store={store}
        actionBarStore={this.actionBarStore}
        FileInputType={FileInputType}
        openImagePreview={this.openImagePreview}
        permitBicStore={this.props.permitBicStore}
      />
    )
  }

  private get formFields(): JSX.Element {
    const { store, isPreviewMode } = this.props
    const { workflowFieldsStore, template } = store
    const { availableWorkflowSteps, editableEntity, existingEntity } =
      workflowFieldsStore

    const stepsToRender = isPreviewMode
      ? template?.workflowSteps || []
      : availableWorkflowSteps
    const { currentWorkflowStepId, isDoneOrDenied } =
      existingEntity || editableEntity

    return (
      <>
        <div className="col my10 px16">
          {this.renderType()}
          {this.renderTitle()}
          {this.renderAiSummary()}
        </div>

        {stepsToRender.map((step, idx) =>
          this.renderWorkflowStep(
            step,
            idx,
            currentWorkflowStepId,
            isDoneOrDenied,
          ),
        )}

        <div className="row no-grow x-end my10">
          {this.renderEditFormButton()}
        </div>

        {this.isExistingAndPublishedForm && (
          <div className="col mb10">
            <PermitInspectionsSection store={store} />
          </div>
        )}
      </>
    )
  }

  private renderWorkflowStep = (
    step: IWorkflowStep,
    idx: number,
    currentWorkflowStepId: string,
    isDoneOrDenied: boolean,
  ): JSX.Element => {
    const { store, isViewMode, isPreviewMode } = this.props
    const { workflowFieldsStore, template } = store
    const { availableWorkflowSteps, isSectionHidden, toggleStepSection } =
      workflowFieldsStore
    const { stepsWithoutRecurring } = template

    const stepsForLevel = isPreviewMode
      ? template?.workflowSteps || []
      : stepsWithoutRecurring

    const availableStep = availableWorkflowSteps.find(s => s.id === step.id)
    const fieldsForStep =
      availableStep?.fields ||
      (isPreviewMode ? step.fields?.filter(f => f.isShown) || [] : [])
    const isCurrentStep = currentWorkflowStepId === step.id
    const isStepCompleted = !isCurrentStep || isDoneOrDenied
    const isViewModeEnabled = isViewMode && !isCurrentStep
    const isStepCollapsed = isSectionHidden(step.id)
    const workflowStepLevel = getWorkflowStepLevel(step.id, stepsForLevel)

    const requireAllSubmissions =
      template?.getStepRequireAllSubmissions(step.id) && !isPreviewMode
    const hasFields = !!fieldsForStep.length
    const hasContent = hasFields || requireAllSubmissions

    return (
      <div
        key={step.id}
        className="col mb10 brada8 mx8 ba-grey-80 overflow-hidden"
        ref={this.setWorkflowStepRef.bind(null, step.id)}
      >
        {this.renderStepHeader(
          step,
          idx,
          workflowStepLevel,
          hasContent,
          isStepCollapsed,
          isStepCompleted,
          toggleStepSection,
        )}
        {!isStepCollapsed &&
          this.renderStepContent(
            step.id,
            fieldsForStep,
            hasFields,
            requireAllSubmissions,
            isStepCompleted,
            isViewModeEnabled,
          )}
      </div>
    )
  }

  private renderStepHeader = (
    step: IWorkflowStep,
    idx: number,
    workflowStepLevel: number,
    hasContent: boolean,
    isStepCollapsed: boolean,
    isStepCompleted: boolean,
    toggleStepSection: (stepId: string) => void,
  ): JSX.Element => {
    const { isPreviewMode } = this.props
    const ChevronIcon = isStepCollapsed ? ChevronRightIcon : ChevronDownIcon

    return (
      <div
        className="row h40 px16 bg-grey-92 pointer no-select"
        onClick={toggleStepSection.bind(null, step.id)}
      >
        {hasContent && <ChevronIcon size={STEP_ICON_SIZE} className="mr8" />}
        <WorkflowStepNameLabel
          className="text extra-large bold line-24"
          stepPosition={idx + 1}
          stepName={getWorkflowStepDisplayName(step.type, workflowStepLevel)}
        />
        {isStepCompleted && !isPreviewMode && (
          <CheckIcon size={STEP_ICON_SIZE} className="ml5 text blue-brand" />
        )}
        {!isStepCompleted && !isPreviewMode && (
          <span className="text large bold orange line-extra-large no-grow nowrap ml5">
            {Localization.translator.current}
          </span>
        )}
      </div>
    )
  }

  private renderStepContent = (
    stepId: string,
    fieldsForStep: IPermitTypeField[],
    hasFields: boolean,
    requireAllSubmissions: boolean,
    isStepCompleted: boolean,
    isViewModeEnabled: boolean,
  ): JSX.Element => {
    const showFields = !requireAllSubmissions || !isStepCompleted
    const showProgressWithSubmissions = requireAllSubmissions

    return (
      <div className="col">
        {showFields &&
          this.renderStepFields(
            stepId,
            fieldsForStep,
            hasFields,
            isViewModeEnabled,
          )}
        {showProgressWithSubmissions &&
          this.renderStepProgress(stepId, isStepCompleted)}
      </div>
    )
  }

  private renderStepFields = (
    stepId: string,
    fieldsForStep: IPermitTypeField[],
    hasFields: boolean,
    isViewModeEnabled: boolean,
  ): JSX.Element => {
    const { store, isReadonly, shouldHideResponsible } = this.props

    return (
      <div
        className={classList({
          'col fields-section px16': true,
          my10: hasFields,
        })}
      >
        {fieldsForStep.map(typeField => (
          <PermitFormFieldWrapper
            key={typeField.id}
            typeField={typeField}
            store={store}
            fieldsStore={store.workflowFieldsStore}
            shouldHideResponsible={shouldHideResponsible}
            isViewMode={isReadonly || isViewModeEnabled}
          />
        ))}
      </div>
    )
  }

  private renderEditFormButton = (): JSX.Element => {
    const { isViewMode, isReadonly, toggleMode } = this.props

    if (!isViewMode || isReadonly) {
      return
    }

    return (
      <Button variant="primary-outline" className="mx20" onClick={toggleMode}>
        <SquarePenIcon className="row no-grow" data-icon="inline-start" />
        {editForm}
      </Button>
    )
  }

  private renderStepProgress = (
    stepId: string,
    isStepCompleted: boolean,
  ): JSX.Element => {
    const { store, permitBicStore } = this.props
    const { existingPermit, template } = store

    if (!existingPermit || !template || !permitBicStore) {
      return null
    }

    const stepRuleIds = template.getStepRuleIds(stepId)
    const completionMode = template.getStepCompletionMode(stepId)
    const isOnePerRule = completionMode === BicCompletionMode.OnePerRule
    const submissionGroups = isOnePerRule
      ? permitBicStore.getApprovalStatusGroupedByRule(
          existingPermit,
          stepId,
          stepRuleIds,
        )
      : permitBicStore.getApprovalStatusGroupedByField(
          existingPermit,
          stepId,
          stepRuleIds,
          template,
        )

    let approved = 0
    let total = 0
    submissionGroups.forEach(group => {
      const signedCount = group.approvedUsers.length
      const requiredCount =
        group.requiredCount ?? signedCount + group.pendingUsers.length
      approved += Math.min(signedCount, requiredCount)
      total += requiredCount
    })

    if (total === 0) {
      return null
    }

    const progressPercent = Math.round((approved / total) * 100)
    const isAllComplete = approved === total && total > 0
    const isPartialComplete = approved > 0 && approved < total
    // If step is completed, always show expanded and don't allow collapse
    const isCollapsed = isStepCompleted
      ? false
      : !this.stepProgressCollapsed.get(stepId)
    const isCollapsible = !isStepCompleted

    const getStatusIcon = () => {
      if (isAllComplete) return CircleCheckIcon
      if (isPartialComplete) return BanIcon
      return CircleIcon
    }

    const StatusIcon = getStatusIcon()
    const ChevronIcon = isCollapsed ? ChevronRightIcon : ChevronDownIcon

    return (
      <div className="step-progress-container px16 py8">
        <div
          className={classList({
            'step-progress-header': true,
            pointer: isCollapsible,
          })}
          onClick={
            isCollapsible
              ? () => this.toggleStepProgressCollapsed(stepId)
              : undefined
          }
        >
          <div className="row y-center">
            {isCollapsible && <ChevronIcon size={16} className="no-grow mr8" />}
            {/* Worded as the step's ball-in-court rules set it */}
            <div className="text large bold">
              {`${Localization.translator.completedBy}: ${getBicCompletionModeLabel(
                completionMode,
                permitBicStore.getRequiredAssigneeKinds(
                  existingPermit,
                  stepRuleIds,
                ),
              )}`}
            </div>
          </div>
          <div className="row x-between y-center mb4">
            <span className="text large light">
              {Localization.translator.xOfYComplete(approved, total)}
            </span>
            <StatusIcon
              size={16}
              className={classList({
                'no-grow ml5 text': true,
                'success-green': isAllComplete,
                light: !isAllComplete,
              })}
            />
          </div>
          <Progress value={progressPercent} variant="success" size="sm" />
        </div>
        <div
          className={classList({
            'step-progress-submissions-wrapper': true,
            collapsed: isCollapsed,
          })}
        >
          <SubmissionStatusList
            submissionGroups={submissionGroups}
            template={template}
            stepId={stepId}
          />
        </div>
      </div>
    )
  }

  private renderType = (): JSX.Element => {
    const { store, isReadonly, isTypeReadOnly } = this.props
    const { editableEntity, template, permitTypeField } =
      store.workflowFieldsStore
    const { id: permitId } = editableEntity

    if (isTypeReadOnly || permitId || isReadonly) {
      const icon = (
        <WorkflowTypeIcon iconName={template?.iconName} size="small" />
      )

      const element = (
        <div className="text large ellipsis">{template?.name}</div>
      )

      return this.renderViewModeRow(formType, icon, element)
    }

    return (
      <StruxhubTextValueSelector
        className="fields-section"
        label={formType}
        isRequired={permitTypeField.isRequired}
        isValid={permitTypeField.isValid}
        isChanged={permitTypeField.isChanged}
        customIconElement={
          <WorkflowTypeIcon iconName={template?.iconName} size="small" />
        }
        value={template?.name}
        onClick={store.showFormTypeSelector}
      />
    )
  }

  private renderTitle(): JSX.Element {
    const { store, isReadonly, isViewMode } = this.props
    const { titleField, editableEntity } = store.workflowFieldsStore
    const { title: permitTitle } = editableEntity

    if (isReadonly || isViewMode) {
      const icon = <PermitFieldIcon fieldName={PermitFieldType.InputText} />
      const element = (
        <div className="text large ellipsis">{permitTitle || '-'}</div>
      )

      return this.renderViewModeRow(_title, icon, element)
    }

    return (
      <StruxhubInput
        isRequired={titleField.isRequired}
        isChanged={titleField.isChanged}
        isValid={titleField.isValid}
        label={_title}
        value={permitTitle}
        onChange={this.onTitleChange}
        onValueReset={this.onTitleReset}
        className="fields-section"
        testId="workflow-title-input"
      />
    )
  }

  private renderViewModeRow = (
    title: string,
    icon: JSX.Element,
    element: JSX.Element | JSX.Element[],
    extraIcon?: JSX.Element,
  ): JSX.Element => {
    if (!element) {
      return
    }

    return (
      <div className="row tw:gap-2.5 no-grow y-start py12">
        <span className="tw:flex tw:size-[26px] tw:flex-none! tw:items-center tw:justify-center">
          {icon}
        </span>
        <div className="col pr6 overflow-hidden">
          <div className="text light large pb4 w-max-content">{title}</div>
          <div>{element}</div>
        </div>
        {extraIcon}
      </div>
    )
  }

  private renderAiSummary(): JSX.Element {
    const { editablePermit, workflowFieldsStore } = this.props.store

    if (
      !this.canAccessAiSummary ||
      !workflowFieldsStore.template ||
      !editablePermit?.id
    ) {
      return null
    }

    return (
      <WorkflowSummary
        key={editablePermit.id}
        permit={editablePermit}
        workflowFieldsStore={workflowFieldsStore}
        companiesStore={this.props.companiesStore}
        projectMembersStore={this.props.projectMembersStore}
        locationAttributesStore={this.props.locationAttributesStore}
      />
    )
  }

  @computed
  private get isExistingAndPublishedForm(): boolean {
    const { template, existingPermit } = this.localProps.store
    return !!template && !!existingPermit?.id && !existingPermit.isDraft
  }

  @computed
  private get isRecurringWorkflow(): boolean {
    return !!this.localProps.store.editablePermit?.recurringSettingsId
  }

  @action.bound
  private openImagePreview(ev: React.MouseEvent<HTMLElement>, url: string) {
    ev.stopPropagation()
    ev.preventDefault()

    this.props.eventsStore.dispatch(
      SHOW_FULLSCREEN_PREVIEW,
      [{ fileUrl: url, fileType: FileType.Image }],
      0,
    )
  }

  private setContainerRef = (ref: HTMLDivElement) => {
    this.containerRef = ref
  }

  private setWorkflowStepRef = (stepId: string, element: HTMLDivElement) => {
    const { currentWorkflowStepId } = this.props.store.editablePermit

    if (currentWorkflowStepId === stepId) {
      this.workflowStepRef = element
    }
  }

  private onTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.props.store.editablePermit.title = event.target.value
  }

  private onTitleReset = () => {
    this.props.store.editablePermit.title = ''
  }

  private get canAccessAiSummary(): boolean {
    const appState = this.localProps.eventsStore?.appState
    return !!appState?.isStruxhubUser && appState.isWorkflowSummaryEnabled
  }

  private scrollToBottom = () => {
    if (!this.isExistingAndPublishedForm || !this.containerRef) {
      return
    }

    const { activeDetailsTab, editablePermit } = this.props.store
    if (
      !editablePermit.isDoneOrDenied &&
      activeDetailsTab === FormDetailsTab.FORM &&
      this.workflowStepRef
    ) {
      this.workflowStepRef.scrollIntoView(true)
      return
    }

    const { scrollHeight, clientHeight } = this.containerRef
    this.containerRef.scrollTop = scrollHeight - clientHeight
  }

  private onHideNonWorkTimeDialog = () => {
    const { store, onPermitClose } = this.props
    const {
      areNonWorkAndDeadlineModalsShown,
      closeDeadlineModal,
      closeNonWorkTimeModal,
    } = store

    if (areNonWorkAndDeadlineModalsShown) {
      closeDeadlineModal()
    }

    closeNonWorkTimeModal()
    onPermitClose?.()
  }

  private onHideDeadlineModal = () => {
    const { store, onPermitClose } = this.props

    store?.closeDeadlineModal()
    onPermitClose?.()
  }

  @action.bound
  private handleDragEnter(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()

    if (this.props.isReadonly) return

    this.isDragOver = true
  }

  @action.bound
  private handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()

    // Only set to false if leaving the container itself (not child elements)
    if (e.currentTarget === e.target) {
      this.isDragOver = false
    }
  }

  @action.bound
  private handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
  }

  @action.bound
  private async handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()

    this.isDragOver = false

    if (this.props.isReadonly) return

    const files = Array.from(e.dataTransfer?.files || [])
    if (files.length === 0) return

    // Upload all files to the first available file field
    await this.uploadFilesToFirstAvailableField(files)
  }

  @action.bound
  private async handlePaste(e: ClipboardEvent) {
    // Only handle paste if the form is active and not readonly
    if (this.props.isReadonly || !this.containerRef) return

    // Don't handle paste if user is typing in an input field
    const activeElement = document.activeElement
    if (
      activeElement instanceof HTMLInputElement ||
      activeElement instanceof HTMLTextAreaElement ||
      activeElement?.getAttribute('contenteditable') === 'true'
    ) {
      return
    }

    const items = Array.from(e.clipboardData?.items || [])
    const files: File[] = []

    // Look for images and files in clipboard
    for (const item of items) {
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault()

        const blob = item.getAsFile()
        if (blob) {
          const timestamp = new Date().getTime()
          const ext = blob.type.split('/')[1] || 'png'
          const file = new File([blob], `screenshot-${timestamp}.${ext}`, {
            type: blob.type,
          })
          files.push(file)
        }
      } else if (item.kind === 'file') {
        const file = item.getAsFile()
        if (file) {
          files.push(file)
        }
      }
    }

    if (files.length > 0) {
      await this.uploadFilesToFirstAvailableField(files)
    }
  }

  private async uploadFilesToFirstAvailableField(files: File[]) {
    const { store } = this.props
    const { workflowFieldsStore } = store

    // Find the first file field in the current workflow step
    const firstFileField = this.findFirstFileField()

    if (!firstFileField) {
      console.warn('No file field available in the current form')
      return
    }

    // Upload all files to the first available file field
    for (const file of files) {
      await store.uploadAttachment(
        file,
        firstFileField,
        workflowFieldsStore,
        null,
        null,
      )
    }
  }

  private findFirstFileField(): IPermitTypeField | null {
    const { store } = this.props
    const { workflowFieldsStore, template } = store
    const { availableWorkflowSteps } = workflowFieldsStore

    if (!template) return null

    // Search through all available workflow steps for the first file field
    for (const step of availableWorkflowSteps) {
      for (const field of step.fields || []) {
        if (field.type === PermitFieldType.File && field.isShown) {
          return field
        }
      }
    }

    return null
  }
}
